import { useMemo, useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Switch } from "../ui/switch";
import { Plus, MagnifyingGlass, CircleNotch } from "@phosphor-icons/react";

/** Controlled, searchable airport picker. Stores the standardized IATA code only; never free text. */
function AirportSelect({ airports, value, onChange, placeholder, testid }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const blurTimer = useRef(null);
  const selected = airports.find((a) => a.code === value) || null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = !q
      ? airports
      : airports.filter(
          (a) =>
            a.code.toLowerCase().includes(q) ||
            (a.city || "").toLowerCase().includes(q) ||
            (a.airport_name || "").toLowerCase().includes(q)
        );
    return list.slice(0, 60);
  }, [query, airports]);

  const inputValue = open ? query : selected ? `${selected.code} — ${selected.city}` : "";

  return (
    <div className="relative">
      <div className="flex items-center gap-2 input px-3">
        <MagnifyingGlass size={14} className="text-[hsl(var(--muted))] shrink-0" />
        <input
          className="bg-transparent outline-none w-full py-2 text-sm"
          placeholder={placeholder}
          value={inputValue}
          data-testid={testid}
          onFocus={() => {
            setOpen(true);
            setQuery("");
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onBlur={() => {
            blurTimer.current = setTimeout(() => setOpen(false), 150);
          }}
        />
      </div>
      {open && (
        <div
          className="absolute z-50 mt-1 w-full max-h-64 overflow-auto rounded-[2px] border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-lg"
          data-testid={`${testid}-list`}
          onMouseDown={(e) => {
            // keep focus so the input blur timer doesn't fire before the click selects
            e.preventDefault();
            clearTimeout(blurTimer.current);
          }}
        >
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-[hsl(var(--muted))]">No matching airport</div>
          ) : (
            filtered.map((a) => (
              <button
                type="button"
                key={a.code}
                className="w-full text-left px-3 py-2 text-sm hover:bg-[hsl(var(--info-bg))] flex flex-col"
                data-testid={`${testid}-option-${a.code}`}
                onClick={() => {
                  onChange(a.code);
                  setOpen(false);
                  setQuery("");
                }}
              >
                <span>
                  <span className="mono font-semibold">{a.code}</span>
                  <span className="text-[hsl(var(--muted))]"> — {a.city}</span>
                </span>
                <span className="text-xs text-[hsl(var(--muted))] truncate">{a.airport_name}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/** Build the post-save endpoint-activation note matching the agreed copy. */
function savedNote(originCode, destCode, originActive, destActive, flagOn) {
  if (!flagOn) {
    return "Route saved. Turn on the route flag to make it eligible for booking after endpoint airports are active.";
  }
  if (!originActive && !destActive) {
    return `Route saved, but not bookable yet. Activate ${originCode} as an active origin and ${destCode} as an active destination first.`;
  }
  if (!originActive) {
    return `Route saved, but not bookable yet. Activate ${originCode} as an active source airport first.`;
  }
  if (!destActive) {
    return `Route saved, but not bookable yet. Activate ${destCode} as an active destination airport first.`;
  }
  return `Route ${originCode} → ${destCode} is now ACTIVE and bookable.`;
}

export default function AddRouteDialog({ airports, onCreated }) {
  const [open, setOpen] = useState(false);
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [flagOn, setFlagOn] = useState(false);
  const [limit, setLimit] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const sameAirport = origin && destination && origin === destination;
  const canSubmit = origin && destination && !sameAirport && !submitting;

  // Standardized display label is auto-derived from the airport master (city -> name -> code).
  const part = (code) => {
    const a = airports.find((x) => x.code === code);
    return a ? (a.city || a.airport_name || a.code) : code;
  };
  const displayLabel =
    origin && destination ? `${part(origin)} \u2192 ${part(destination)}` : "";

  const reset = () => {
    setOrigin("");
    setDestination("");
    setFlagOn(false);
    setLimit("");
  };

  const submit = async () => {
    if (!canSubmit) return;
    const trimmed = String(limit).trim();
    if (trimmed !== "" && (!/^\d+$/.test(trimmed) || Number(trimmed) < 1)) {
      toast.error("Daily shipment limit must be a positive whole number (or blank for Unlimited)");
      return;
    }
    setSubmitting(true);
    try {
      // route_label is intentionally omitted — the backend auto-derives the standardized label.
      await api.post("/admin/routes", {
        source_airport_code: origin,
        destination_airport_code: destination,
        is_active: flagOn,
        daily_shipment_limit: trimmed === "" ? null : Number(trimmed),
      });
      const oa = airports.find((a) => a.code === origin)?.is_active_source;
      const da = airports.find((a) => a.code === destination)?.is_active_destination;
      toast.success(`Route ${origin} → ${destination} saved`, {
        description: savedNote(origin, destination, !!oa, !!da, flagOn),
        duration: 7000,
      });
      reset();
      setOpen(false);
      onCreated && (await onCreated());
    } catch (e) {
      toast.error(e.displayMessage || "Failed to add route");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        className="btn-primary inline-flex items-center gap-1.5 text-sm"
        onClick={() => setOpen(true)}
        data-testid="add-route-button"
      >
        <Plus size={14} weight="bold" /> Add Route
      </button>

      <Dialog
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) reset();
        }}
      >
        <DialogContent className="sm:max-w-[460px]" data-testid="add-route-dialog">
          <DialogHeader>
            <DialogTitle>Add a directional route</DialogTitle>
            <DialogDescription>
              Direction matters — {origin || "ORIGIN"} → {destination || "DEST"} is different from its
              reverse. Creating a route does not activate airports or the reverse route.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-1">
            <div>
              <label className="overline block mb-1">Source airport</label>
              <AirportSelect
                airports={airports}
                value={origin}
                onChange={setOrigin}
                placeholder="Search code / city / name…"
                testid="add-route-origin"
              />
            </div>
            <div>
              <label className="overline block mb-1">Destination airport</label>
              <AirportSelect
                airports={airports}
                value={destination}
                onChange={setDestination}
                placeholder="Search code / city / name…"
                testid="add-route-destination"
              />
            </div>
            {sameAirport && (
              <p className="text-xs text-red-600" data-testid="add-route-error">
                Origin and destination must be different airports.
              </p>
            )}
            {origin && destination && !sameAirport && (
              <div
                className="rounded-[2px] border border-[hsl(var(--border))] bg-[hsl(var(--info-bg))] p-3 text-sm"
                data-testid="add-route-preview"
              >
                <div>
                  <span className="text-[hsl(var(--muted))]">Route: </span>
                  <span className="mono font-semibold" data-testid="add-route-preview-code">
                    {origin} → {destination}
                  </span>
                </div>
                <div>
                  <span className="text-[hsl(var(--muted))]">Display: </span>
                  <span className="font-medium" data-testid="add-route-preview-label">{displayLabel}</span>
                </div>
                <p className="text-xs text-[hsl(var(--muted))] mt-1">
                  Label is auto-generated from the airport master and saved automatically.
                </p>
              </div>
            )}
            <div className="flex items-center justify-between rounded-[2px] border border-[hsl(var(--border))] p-3">
              <div>
                <div className="text-sm font-medium">Route flag</div>
                <div className="text-xs text-[hsl(var(--muted))]">
                  Off by default. A route is bookable only when this is on and both endpoint airports
                  are active for their directions.
                </div>
              </div>
              <Switch checked={flagOn} onCheckedChange={setFlagOn} data-testid="add-route-flag-toggle" />
            </div>
            <div className="rounded-[2px] border border-[hsl(var(--border))] p-3">
              <label className="text-sm font-medium block mb-1" htmlFor="add-route-limit">
                Daily shipment limit (optional)
              </label>
              <input
                id="add-route-limit"
                type="number" min="1" step="1"
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                placeholder="Unlimited"
                className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded text-sm"
                data-testid="add-route-limit-input"
              />
              <div className="text-xs text-[hsl(var(--muted))] mt-1">
                Blank means Unlimited. Positive whole number caps shipments booked per IST day.
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-2">
            <button className="btn-ghost text-sm" onClick={() => setOpen(false)} data-testid="add-route-cancel">
              Cancel
            </button>
            <button
              className="btn-primary inline-flex items-center gap-1.5 text-sm"
              disabled={!canSubmit}
              onClick={submit}
              data-testid="add-route-submit"
            >
              {submitting ? <CircleNotch size={14} className="animate-spin" /> : <Plus size={14} weight="bold" />}
              Save route
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
